import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { addDays, startOfWeek, format } from "date-fns";
import { it } from "date-fns/locale";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { SHIFT_KINDS, isoDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const useAvailability = (dateFrom, dateTo) => {
  const [map, setMap] = useState({});
  const load = useCallback(() => {
    api.get("/hr/availability", { params: { date_from: dateFrom, date_to: dateTo } }).then((r) => setMap(r.data)).catch(() => setMap({}));
  }, [dateFrom, dateTo]);
  useEffect(() => { if (dateFrom && dateTo) load(); }, [load, dateFrom, dateTo]);
  return [map, load];
};

const ShiftCell = ({ emp, day, entry, canPlan, onSet }) => {
  const absent = entry?.kind === "ABSENCE";
  const value = entry?.kind === "SHIFT" ? entry.code : "none";
  return (
    <div className={`border-b border-r p-1.5 ${absent ? (entry.pending ? "bg-amber-50" : "bg-red-50") : ""}`}>
      {absent && <div className={`mb-1 truncate text-[10px] font-semibold uppercase ${entry.pending ? "text-amber-800" : "text-red-700"}`} data-testid={`shift-absence-${emp.id}-${day}`}>{entry.label}</div>}
      {!(absent && !entry.pending) && (
        <Select value={value} onValueChange={(v) => onSet(emp.id, day, v === "none" ? null : v)} disabled={!canPlan}>
          <SelectTrigger className="h-8 bg-white text-xs" data-testid={`shift-cell-${emp.id}-${day}`}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="none">Standard</SelectItem>{Object.entries(SHIFT_KINDS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
      )}
    </div>
  );
};

export const ShiftsPanel = () => {
  const { can } = useAuth();
  const { employees, maps, companyId } = useData();
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => isoDate(addDays(weekStart, i))), [weekStart]);
  const [avail, reload] = useAvailability(days[0], days[6]);
  const onSet = async (employeeId, day, kind) => {
    try {
      await api.post("/hr/shifts", { employee_ids: [employeeId], date_from: day, date_to: day, kind });
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div data-testid="shifts-panel">
      <div className="mb-3 flex items-center gap-2">
        <Button variant="outline" size="icon" onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="shifts-prev-week"><ChevronLeft className="h-4 w-4" /></Button>
        <div className="min-w-[200px] text-center font-mono text-sm" data-testid="shifts-week-label">{format(weekStart, "d MMM", { locale: it })} — {format(addDays(weekStart, 6), "d MMM yyyy", { locale: it })}</div>
        <Button variant="outline" size="icon" onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="shifts-next-week"><ChevronRight className="h-4 w-4" /></Button>
        <span className="ml-3 text-xs text-muted-foreground">"Standard" = disponibile in tutte le fasce. Le assenze approvate bloccano la giornata.</span>
      </div>
      <div className="overflow-x-auto border-l border-t bg-white">
        <div className="grid min-w-[1000px]" style={{ gridTemplateColumns: "200px repeat(7, minmax(110px, 1fr))" }}>
          <div className="border-b border-r bg-muted/50 px-3 py-2 micro-label">Tecnico</div>
          {days.map((d) => (
            <div key={d} className="border-b border-r bg-muted/50 px-3 py-2">
              <div className="text-[10px] uppercase tracking-[0.18em] opacity-70">{format(new Date(`${d}T00:00:00`), "EEEE", { locale: it })}</div>
              <div className="font-mono text-sm font-semibold">{format(new Date(`${d}T00:00:00`), "dd/MM")}</div>
            </div>
          ))}
          {employees.filter((e) => e.active !== false && (!companyId || e.company_id === companyId)).map((e) => (
            <div key={e.id} className="contents">
              <div className="border-b border-r px-3 py-2"><div className="text-sm font-semibold">{e.name}</div><div className="text-[11px] text-muted-foreground">{maps.companies?.[e.company_id]?.code}</div></div>
              {days.map((d) => <ShiftCell key={d} emp={e} day={d} entry={avail[`${e.id}|${d}`]} canPlan={can("hr:plan")} onSet={onSet} />)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
